import { useEffect, useRef, useState } from 'react';
import { Icon, ThumbnailButton } from '@vds/components';

import {
    type DebugErrorDetail,
    formatDebugError,
    getDebugErrors,
    subscribeDebugError,
} from '@/utils/devtools/debug-error-dev-tool';

import styles from './DevDrawer.module.css';

function getDebugErrorSectionTitle(errorCount: number): string {
    return errorCount === 1 ? 'There is an Error on this Page!' : 'There are Errors on this Page!';
}

function getLatestErrorIndex(errorCount: number): number {
    return Math.max(0, errorCount - 1);
}

export default function DebugErrorDevTool() {
    const [errors, setErrors] = useState<DebugErrorDetail[]>(() => [...getDebugErrors()]);
    const [activeIndex, setActiveIndex] = useState(() =>
        getLatestErrorIndex(getDebugErrors().length),
    );
    const previousErrorCountRef = useRef(errors.length);

    useEffect(
        () =>
            subscribeDebugError((nextErrors) => {
                const nextErrorCount = nextErrors.length;
                const previousErrorCount = previousErrorCountRef.current;

                setErrors([...nextErrors]);

                if (nextErrorCount > previousErrorCount) {
                    setActiveIndex(getLatestErrorIndex(nextErrorCount));
                } else if (nextErrorCount < previousErrorCount) {
                    setActiveIndex((currentIndex) =>
                        Math.min(currentIndex, getLatestErrorIndex(nextErrorCount)),
                    );
                }

                previousErrorCountRef.current = nextErrorCount;
            }),
        [],
    );

    if (errors.length === 0) {
        return null;
    }

    const activeError = errors[activeIndex] ?? errors[errors.length - 1];
    const errorCount = errors.length;
    const errorPosition = activeIndex + 1;
    const errorCountLabel = `${errorPosition} of ${errorCount} error${errorCount === 1 ? '' : 's'}`;

    const isPreviousDisabled = errorPosition <= 1;
    const isNextDisabled = errorPosition >= errorCount;

    const goToPreviousError = () => {
        if (isPreviousDisabled) {
            return;
        }

        setActiveIndex((currentIndex) => Math.max(0, currentIndex - 1));
    };

    const goToNextError = () => {
        if (isNextDisabled) {
            return;
        }

        setActiveIndex((currentIndex) => Math.min(errorCount - 1, currentIndex + 1));
    };

    return (
        <div className={styles.errorSection}>
            <div className={styles.errorSectionHeading}>
                <Icon aria-hidden className={styles.errorSectionIcon} name="alert" size="medium" />
                <h2 className={styles.errorSectionTitle}>
                    {getDebugErrorSectionTitle(errorCount)}
                </h2>
            </div>
            <div className={styles.errorNavigation}>
                <ThumbnailButton
                    aria-label="Previous error"
                    className={`${styles.errorNavButton} ${
                        isPreviousDisabled ? styles.errorNavButtonDisabled : ''
                    }`}
                    icon="chevron-backward"
                    label="Previous error"
                    onClick={goToPreviousError}
                />
                <span className={styles.errorNavigationLabel}>{errorCountLabel}</span>
                <ThumbnailButton
                    aria-label="Next error"
                    className={`${styles.errorNavButton} ${
                        isNextDisabled ? styles.errorNavButtonDisabled : ''
                    }`}
                    icon="chevron-forward"
                    label="Next error"
                    onClick={goToNextError}
                />
            </div>
            <textarea
                aria-label="Debug error stack trace"
                className={styles.errorTextarea}
                readOnly
                value={formatDebugError(activeError)}
            />
        </div>
    );
}
